/**
 * Contratos (schemas/DTOs) da fatia "billing-admin": assinatura do lojista,
 * webhooks de cobrança e API do superadmin (/admin-api).
 * Os schemas básicos (checkout, planos, cupons, auditoria...) ficam em ../schemas/billing.ts.
 */
import { z } from 'zod';
import { optionalText, paginationQuery, requiredText } from '../schemas/common';
import type { BillingCycleKey, Entitlements, FeatureMap, LimitMap, SubscriptionStatusKey } from '../plans';
import type { AdminPermissionKey, AdminRoleKey, RoleKey } from '../permissions';
import type {
  AdminMeDTO,
  AuditLogDTO,
  CheckoutSessionDTO,
  InvoiceDTO,
  PlanDTO,
  SubscriptionDTO,
  UsageDTO,
} from '../types';

// =============================================================================
// Schemas
// =============================================================================

const subscriptionStatusEnum = z.enum(['trialing', 'active', 'past_due', 'canceled', 'expired']);
const invoiceStatusEnum = z.enum(['pending', 'paid', 'overdue', 'canceled', 'refunded']);
const isoDate = z.string().datetime();

/** POST /admin-api/tenants/:id/schedule-deletion */
export const scheduleDeletionSchema = z.object({
  reason: requiredText(300, 'Informe o motivo'),
  /** Carência em dias até a exclusão definitiva (0 = na próxima rotina de limpeza). */
  days: z.number().int().min(0).max(90).default(30),
});

/** POST /admin-api/tenants/:id/send-password-reset (padrão: dono da loja). */
export const tenantUserTargetSchema = z.object({ userId: z.string().uuid().optional() });

/** Motivo opcional para ações administrativas simples (cancelar/estornar fatura, reativar...). */
export const adminReasonSchema = z.object({ reason: optionalText(300) });

export const adminUserListQuery = paginationQuery.extend({
  q: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'disabled']).optional(),
});

export const adminSubscriptionListQuery = paginationQuery.extend({
  q: z.string().trim().max(100).optional(),
  status: subscriptionStatusEnum.optional(),
  planCode: z.string().max(40).optional(),
});

export const adminInvoiceListQuery = paginationQuery.extend({
  q: z.string().trim().max(100).optional(),
  status: invoiceStatusEnum.optional(),
  companyId: z.string().uuid().optional(),
  provider: z.string().max(20).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});

export const adminFeedbackListQuery = paginationQuery.extend({
  type: z.enum(['bug', 'idea', 'question', 'other', 'abuse']).optional(),
  status: z.enum(['new', 'read', 'resolved']).optional(),
  companyId: z.string().uuid().optional(),
});

export const feedbackStatusSchema = z.object({ status: z.enum(['new', 'read', 'resolved']) });

export const adminJobListQuery = paginationQuery.extend({
  status: z.enum(['pending', 'running', 'done', 'failed', 'dead']).default('failed'),
  name: z.string().max(80).optional(),
});

export const webhookEventListQuery = paginationQuery.extend({
  provider: z.string().max(20).optional(),
  state: z.enum(['processed', 'error', 'invalid', 'pending']).optional(),
  eventType: z.string().max(80).optional(),
});

// =============================================================================
// Assinatura do lojista (/api/billing)
// =============================================================================

export interface BillingOverviewDTO {
  subscription: SubscriptionDTO;
  entitlements: Entitlements;
  usage: UsageDTO;
  /** Fatura em aberto (pendente ou vencida) mais recente, para o botão "Pagar". */
  openInvoice: InvoiceDTO | null;
  /** Provedor de cobrança ativo na plataforma (manual | asaas). */
  provider: string;
  /** Tela de pagamento simulado disponível (provedor manual fora de produção). */
  canSimulatePayment: boolean;
}

/**
 * Resultado de POST /api/billing/checkout.
 *  - payment: fatura criada; abra `paymentUrl` (Pix, boleto ou cartão).
 *  - activated: cupom de 100% — plano ativado sem cobrança.
 *  - downgrade_scheduled: plano grátis agendado para o fim do período atual.
 *  - downgraded: a loja já estava sem plano pago; passou ao plano grátis na hora.
 */
export type BillingCheckoutResultDTO =
  | (CheckoutSessionDTO & {
      kind: 'payment';
      amountCents: number;
      discountCents: number;
      couponCode: string | null;
      dueDate: string;
      /** true quando reaproveitamos uma fatura em aberto idêntica. */
      reused: boolean;
    })
  | { kind: 'activated'; invoiceId: string; planCode: string; currentPeriodEnd: string | null }
  | { kind: 'downgrade_scheduled' | 'downgraded'; planCode: string; effectiveAt: string | null };

// =============================================================================
// Superadmin — autenticação
// =============================================================================

export interface AdminMeDetailsDTO extends AdminMeDTO {
  /** A sessão aguarda o código do app autenticador (POST /auth/mfa). */
  mfaRequired: boolean;
  /** O 2FA é obrigatório e ainda não foi configurado (POST /auth/mfa/setup → /auth/mfa/enable). */
  mfaSetupRequired: boolean;
  permissions: AdminPermissionKey[];
  sessionExpiresAt: string;
}

export interface AdminLoginResultDTO {
  mfaRequired: boolean;
  mfaSetupRequired: boolean;
  me: AdminMeDetailsDTO;
}

export interface AdminMfaSetupDTO {
  secret: string;
  otpauthUrl: string;
}

export interface AdminMfaEnabledDTO {
  recoveryCodes: string[];
  me: AdminMeDetailsDTO;
}

// =============================================================================
// Superadmin — lojas (tenants)
// =============================================================================

export interface AdminNoteDTO {
  id: string;
  body: string;
  adminId: string;
  adminName: string;
  createdAt: string;
}

export interface AdminInvoiceDTO extends InvoiceDTO {
  /** Nulo quando a loja foi excluída definitivamente (a fatura é mantida por obrigação fiscal). */
  companyId: string | null;
  companyName: string | null;
  billingName: string | null;
  billingDocument: string | null;
  billingEmail: string | null;
  planCode: string | null;
  provider: string;
  providerPaymentId: string | null;
  notes: string | null;
  updatedAt: string;
}

export interface AdminTenantSubscriptionDTO extends SubscriptionDTO {
  overrides: { features?: Partial<FeatureMap>; limits?: Partial<LimitMap> };
  canceledAt: string | null;
  cancelReason: string | null;
  providerCustomerId: string | null;
  createdAt: string;
}

export interface AdminTenantDetailDTO {
  company: {
    id: string;
    name: string;
    legalName: string | null;
    document: string | null;
    segment: string | null;
    email: string | null;
    phone: string | null;
    whatsapp: string | null;
    city: string | null;
    state: string | null;
    logoUrl: string | null;
    timezone: string;
    status: 'active' | 'suspended' | 'canceled';
    suspendedReason: string | null;
    suspendedAt: string | null;
    allowSupportAccess: boolean;
    deletionRequestedAt: string | null;
    deletionScheduledFor: string | null;
    catalogSlug: string | null;
    /** Endereço público real do catálogo (subdomínio ou domínio próprio verificado). */
    catalogUrl: string | null;
    catalogPublished: boolean;
    createdAt: string;
    updatedAt: string;
  };
  owner: { id: string; name: string; email: string; phone: string | null; status: 'active' | 'disabled'; mfaEnabled: boolean; lastLoginAt: string | null } | null;
  members: {
    membershipId: string;
    userId: string;
    name: string;
    email: string;
    role: RoleKey;
    status: 'active' | 'disabled';
    lastLoginAt: string | null;
    createdAt: string;
  }[];
  subscription: AdminTenantSubscriptionDTO | null;
  entitlements: Entitlements | null;
  usage: UsageDTO;
  stats: { customersCount: number; ordersLast30d: number; gmvLast30dCents: number };
  invoices: AdminInvoiceDTO[];
  audit: AuditLogDTO[];
  notes: AdminNoteDTO[];
}

// =============================================================================
// Superadmin — usuários dos lojistas
// =============================================================================

export interface AdminUserListItemDTO {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  status: 'active' | 'disabled';
  emailVerified: boolean;
  mfaEnabled: boolean;
  companiesCount: number;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  lockedUntil: string | null;
  createdAt: string;
}

export interface AdminUserDetailDTO extends AdminUserListItemDTO {
  memberships: {
    membershipId: string;
    companyId: string;
    companyName: string;
    companyStatus: 'active' | 'suspended' | 'canceled';
    role: RoleKey;
    status: 'active' | 'disabled';
    isOwner: boolean;
    createdAt: string;
  }[];
  sessions: {
    id: string;
    clientType: 'web' | 'mobile' | 'desktop';
    ip: string | null;
    userAgent: string | null;
    createdAt: string;
    lastSeenAt: string;
    impersonated: boolean;
  }[];
  recentActivity: AuditLogDTO[];
}

// =============================================================================
// Superadmin — planos, assinaturas, faturas e cupons
// =============================================================================

export interface AdminPlanDTO extends PlanDTO {
  isPublic: boolean;
  isActive: boolean;
  sortOrder: number;
  /** Lojas com este plano (qualquer status). */
  subscribersCount: number;
  /** Lojas com este plano em teste, ativas ou com pagamento pendente. */
  activeSubscribersCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSubscriptionDTO {
  id: string;
  companyId: string;
  companyName: string;
  planCode: string;
  planName: string;
  status: SubscriptionStatusKey;
  billingCycle: BillingCycleKey;
  priceCents: number;
  mrrCents: number;
  discountPercent: number;
  couponCode: string | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  pastDueSince: string | null;
  provider: string;
  createdAt: string;
}

export interface BillingCouponDTO {
  id: string;
  code: string;
  description: string | null;
  percentOff: number;
  durationCycles: number | null;
  maxRedemptions: number | null;
  redemptionsCount: number;
  planCodes: string[];
  validUntil: string | null;
  isActive: boolean;
  status: 'active' | 'expired' | 'exhausted' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Superadmin — auditoria, equipe da plataforma, sistema
// =============================================================================

export interface AuditVerifyDTO {
  /** Registros selados verificados. */
  checked: number;
  /** Primeiro registro com hash inválido (cadeia adulterada), ou null. */
  firstInvalidId: string | null;
  ok: boolean;
  /** Registros ainda não selados (o worker sela a cada minuto os com mais de 1 min). */
  unsealed: number;
}

export interface AdminAccountDTO {
  id: string;
  email: string;
  name: string;
  role: AdminRoleKey;
  status: 'active' | 'disabled';
  mfaEnabled: boolean;
  allowedIps: string[];
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  lockedUntil: string | null;
  createdAt: string;
  isYou: boolean;
}

export interface AdminSystemSettingsDTO {
  signupEnabled: boolean;
  maintenanceMode: boolean;
  maintenanceMessage: string | null;
  supportWhatsapp: string | null;
  supportEmail: string | null;
  aiEnabled: boolean;
  trialPlanCode: string;
  trialDays: number;
}

export interface FeatureFlagDTO {
  key: string;
  description: string | null;
  enabled: boolean;
  companyIds: string[];
  updatedAt: string;
}

export interface AdminAnnouncementDTO {
  id: string;
  title: string;
  body: string;
  level: 'info' | 'success' | 'warning' | 'critical';
  planCodes: string[];
  link: string | null;
  startsAt: string;
  endsAt: string | null;
  isActive: boolean;
  /** Ativo e dentro da janela de exibição agora. */
  live: boolean;
  createdById: string | null;
  createdAt: string;
}

export interface AdminFeedbackDTO {
  id: string;
  type: 'bug' | 'idea' | 'question' | 'other' | 'abuse';
  message: string;
  pageUrl: string | null;
  status: 'new' | 'read' | 'resolved';
  companyId: string | null;
  companyName: string | null;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  createdAt: string;
}

// =============================================================================
// Superadmin — fila de jobs e webhooks
// =============================================================================

export type JobStatusKey = 'pending' | 'running' | 'done' | 'failed' | 'dead';

export interface AdminJobDTO {
  id: string;
  name: string;
  queue: string;
  status: JobStatusKey;
  attempts: number;
  maxAttempts: number;
  lastError: string | null;
  runAt: string;
  createdAt: string;
  finishedAt: string | null;
  lockedBy: string | null;
  /** Payload resumido (e-mails mostram só destinatário e assunto — nunca links/tokens). */
  payloadPreview: Record<string, unknown>;
}

export interface AdminJobStatsDTO {
  byStatus: Record<JobStatusKey, number>;
  byName: ({ name: string } & Record<JobStatusKey, number>)[];
  oldestPendingAt: string | null;
}

export interface WebhookEventDTO {
  id: string;
  provider: string;
  externalId: string | null;
  eventType: string;
  signatureValid: boolean;
  processedAt: string | null;
  error: string | null;
  receivedAt: string;
  /** Somente no detalhe (GET /webhook-events/:id). */
  payload?: unknown;
}
